/**!*******************************************************************
 *
 * Copyright (c) Epic AI GmbH. All rights reserved.
 * Author:  Alexander Claas
 *
 ********************************************************************!*/

import { IBlock, IStepFlow, IStepIntent } from "@chatcaptain/types/chatbot";
import {
  addDoc,
  collection,
  getFirestore,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { store } from "@/store";
import { BotStore } from "@/store/constants";
import { XYPosition } from "@braks/vue-flow";
import { createIntent, deleteIntent } from "@/api/bot/intents";
import { Block } from "@/classes/Block";
import {
  createSubFlow,
  deleteOutlet,
  deleteSubFlow,
  updateOutletName,
} from "@/api/bot/flow/flow";

export const createFlowStep = async (
  flowId: string,
  type: string,
  position: XYPosition
): Promise<IBlock> => {
  const data = getIntialContent(type, position);
  const doc = await addDoc(
    collection(
      getFirestore(),
      "dialogs/" +
        store.getters[BotStore.Getters.GET_ID] +
        "/flows/" +
        flowId +
        "/blocks"
    ),
    data
  );
  switch (type) {
    case "intent": {
      const intentId = await createIntent("", flowId, doc.id);
      await updateDoc(doc, {
        intent_id: intentId,
        updated: serverTimestamp(),
      });
      return {
        ...data,
        id: doc.id,
        intent_id: intentId,
      } as IStepIntent;
    }
    case "flow": {
      const { flow, entry, outlet } = await createSubFlow("", flowId, doc.id);
      const outlets = {};
      if (outlet.id)
        outlets[outlet.id] = {
          connected: null,
          name: "Ausstieg",
        };
      await updateDoc(doc, {
        updated: serverTimestamp(),
        outlets,
        flow_id: flow.id,
      });
      return {
        ...data,
        ...outlets,
        flow_id: flow.id,
        id: doc.id,
      } as IStepFlow;
    }
    case "outlet": {
      await updateOutletName(flowId, doc.id, "Ausstieg");
    }
  }
  return {
    ...data,
    id: doc.id,
  } as IBlock;
};

export const deleteFlowStep = async (flowId: string, data: IBlock) => {
  switch (data.type) {
    case "intent": {
      const intentData = data as IStepIntent;
      if (intentData.intent_id) await deleteIntent(intentData.intent_id);
      break;
    }
    case "flow": {
      const flowData = data as IStepFlow;
      if (flowData.flow_id) await deleteSubFlow(flowData.flow_id);
      break;
    }
    case "outlet": {
      if (data.id) await deleteOutlet(flowId, data.id);
    }
  }
  if (data.id) await new Block(flowId, data.id).delete();
};

export const getIntialContent = (type: string, position: XYPosition) => {
  let typeBasedContent = {};
  switch (type) {
    case "elements":
      typeBasedContent = { elements: [], redirect: null };
      break;
    case "intent":
      typeBasedContent = { intent_id: null, start: null };
      break;
    case "condition":
      typeBasedContent = { conditions: [], redirects: { T: null, F: null } };
      break;
    case "flow":
      typeBasedContent = { outlets: [] }; //TODO add redirect and flow id
      break;
    case "split":
      typeBasedContent = {
        redirects: { A: null, B: null },
        split: { A: 0.5, B: 0.5 },
        same_redirect: true,
      };
      break;
    case "delay":
      typeBasedContent = { delay: 0, delay_type: "seconds" };
      break;
    case "outlet":
      typeBasedContent = { redirect: null };
      break;
    // types with no extra content: notes
  }
  return {
    created: serverTimestamp(),
    updated: serverTimestamp(),
    type,
    name: "",
    description: "",
    grid: {
      x: position.x,
      y: position.y,
    },
    ...typeBasedContent,
  };
};
